/**
 * useSessionState - Syncs per-session key-value state with the backend.
 *
 * Usage:
 *   const [value, setValue] = useSessionState<MyType>(sessionId, 'project.panel')
 *
 * - On mount / sessionId change: fetches value from GET /api/sessions/:id/state?keys=<key>
 * - setValue does an optimistic local update + debounced PATCH to server
 * - Pass `null` to delete the key
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { getApiUrl } from '@/lib/gateway-url'
import { fetchStateBatched, primeStateValue } from '@/lib/state-batch'

const API_URL = getApiUrl()

function authHeaders(token?: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

export function createSessionStatePersistRequestInit(
  token: string | null | undefined,
  key: string,
  value: unknown,
): RequestInit {
  return {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ [key]: value }),
  }
}

export function shouldApplySessionStateFetchResult(
  fetchWriteVersion: number,
  currentWriteVersion: number,
): boolean {
  return fetchWriteVersion === currentWriteVersion
}

export function getSessionStateRequestKey(
  sessionId: string | null,
  key: string,
  token?: string | null,
): string | null {
  return sessionId && token ? `${sessionId}:${key}:${token}` : null
}

export function isSessionStateLoading(
  loading: boolean,
  requestKey: string | null,
  loadedRequestKey: string | null,
): boolean {
  return loading || (requestKey != null && loadedRequestKey !== requestKey)
}

export function useSessionState<T>(
  sessionId: string | null,
  key: string,
  token?: string | null,
): [T | null, (value: T | null) => void, boolean] {
  const [value, setValueLocal] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadedRequestKey, setLoadedRequestKey] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestRef = useRef<T | null>(null)
  const localWriteVersionRef = useRef(0)
  const requestKey = getSessionStateRequestKey(sessionId, key, token)

  // Fetch on mount / session change
  useEffect(() => {
    if (!sessionId || !token) {
      setValueLocal(null)
      setLoading(false)
      setLoadedRequestKey(null)
      return
    }

    // Cancel any pending debounced PATCH from the previous session —
    // otherwise it fires after latestRef is reset to null below and
    // silently deletes the key (e.g. chat.providerRuntimeMode) for the
    // old session, causing the mode to "reset to default" on next visit.
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }

    // Reset to null immediately so stale values from the previous session
    // don't briefly leak into UI (e.g. terminal panel flash on project switch).
    setValueLocal(null)
    latestRef.current = null
    setLoadedRequestKey(null)

    let cancelled = false
    const fetchVersion = localWriteVersionRef.current
    const nextRequestKey = getSessionStateRequestKey(sessionId, key, token)
    setLoading(true)

    fetchStateBatched('sessions', sessionId, key, token)
      .then((val) => {
        if (cancelled) return
        if (!shouldApplySessionStateFetchResult(fetchVersion, localWriteVersionRef.current)) return
        setValueLocal(val as T | null)
        latestRef.current = val as T | null
      })
      .catch(() => {
        if (!cancelled) setValueLocal(null)
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
          setLoadedRequestKey(nextRequestKey)
        }
      })

    return () => {
      cancelled = true
    }
  }, [sessionId, key, token])

  // Setter: optimistic local + debounced PATCH
  const setValue = useCallback(
    (next: T | null) => {
      localWriteVersionRef.current += 1
      setValueLocal(next)
      latestRef.current = next

      if (!sessionId || !token) return
      primeStateValue('sessions', sessionId, token, key, next)

      // Debounce writes to avoid rapid-fire PATCHes
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        fetch(`${API_URL}/api/sessions/${sessionId}/state`, {
          ...createSessionStatePersistRequestInit(token, key, latestRef.current),
        }).catch(() => {
          // Silently ignore write failures - local state stays optimistic
        })
      }, 300)
    },
    [sessionId, key, token],
  )

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  return [value, setValue, isSessionStateLoading(loading, requestKey, loadedRequestKey)]
}
